import { ApiError } from './types.js'

/**
 * Turning a response or a thrown value into one `ApiError`.
 *
 * Split out from the client so it is testable with no `fetch` in sight — the mapping is
 * where the subtle decisions live, and they deserve direct tests rather than tests that
 * have to stand up a server first.
 */

/** `zapio-backend`'s error shapes. One interface, because `useFormErrors` is the only parser. */
interface Envelope {
  readonly error?: {
    readonly code?: unknown
    readonly message?: unknown
    readonly requestId?: unknown
    readonly errors?: unknown
  }
  /** The validation shape its global pipe produces: `{ errors: { field: string[] } }`. */
  readonly errors?: unknown
  readonly message?: unknown
  readonly requestId?: unknown
}

export async function toApiError(response: Response, path: string): Promise<ApiError> {
  // `.json()` is typed `any`, which is exactly the hole `no-unsafe-assignment` exists to
  // close: this body came from another process and nothing has checked its shape yet.
  const body: unknown = await response.json().catch((): null => null)
  const envelope = (body ?? {}) as Envelope
  const inner = envelope.error ?? {}

  return new ApiError(
    asString(inner.code) ?? `HTTP_${String(response.status)}`,
    response.status,
    message(inner, envelope, response, path),
    fieldBag(inner.errors) ?? fieldBag(envelope.errors),
    requestId(inner, envelope, response),
  )
}

function message(
  inner: NonNullable<Envelope['error']>,
  envelope: Envelope,
  response: Response,
  path: string,
): string {
  return (
    asString(inner.message) ??
    asString(envelope.message) ??
    // `statusText` is empty over HTTP/2, so it can never be the only fallback.
    (response.statusText !== '' ? response.statusText : `Request to ${path} failed`)
  )
}

function requestId(
  inner: NonNullable<Envelope['error']>,
  envelope: Envelope,
  response: Response,
): string | undefined {
  return (
    asString(inner.requestId) ??
    asString(envelope.requestId) ??
    response.headers.get('x-request-id') ??
    undefined
  )
}

/**
 * A rejected `fetch`, which is a different thing from a refusal.
 *
 * Both get **`status: 0`** — below every `>= 400` check — because the request did not
 * happen. A caller that treats "offline" as "the server rejected my input" shows a
 * validation error for a dropped Wi-Fi connection.
 *
 * A caller's own abort is rethrown untouched: it is not a failure, it is the caller
 * changing its mind, and turning it into an `ApiError` makes a cancelled search render an
 * error state.
 */
export function toTransportError(error: unknown, path: string, timedOut: boolean): unknown {
  if (error instanceof DOMException && error.name === 'AbortError' && !timedOut) {
    return error
  }

  if (timedOut) {
    return new ApiError('UPSTREAM_TIMEOUT', 0, 'The request took too long. Try again.')
  }

  return new ApiError(
    'UPSTREAM_UNAVAILABLE',
    0,
    'The server could not be reached. Check your connection.',
    undefined,
    path,
  )
}

/**
 * A 2xx whose body will not parse is drift, not success.
 *
 * Resolving `undefined` for a 204 **and** for an empty 200 body is deliberate: the backend
 * answers some writes with no content, and treating that as malformed would fail an
 * action that actually succeeded.
 */
export async function parseBody<T>(response: Response, path: string): Promise<T> {
  if (response.status === 204) return undefined as T

  const text = await response.text()

  if (text === '') return undefined as T

  try {
    return JSON.parse(text) as T
  } catch {
    throw new ApiError(
      'UPSTREAM_MALFORMED',
      response.status,
      `The server sent a response that could not be read (${path}).`,
    )
  }
}

/**
 * Normalises the field bag to `Record<string, string[]>`.
 *
 * **This is where the two backends' shapes are reconciled.** `zapio-backend` sends
 * `{ field: ["message"] }` — an array. The sibling app's backend sends a plain string,
 * which is why its doctrine forbids `errors.email?.[0]`; against this API that rule would
 * render the first *character* of the message. Both shapes are accepted here and leave as
 * arrays, so exactly one parser downstream sees exactly one shape.
 */
function fieldBag(value: unknown): Readonly<Record<string, readonly string[]>> | undefined {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined

  const bag: Record<string, readonly string[]> = {}

  for (const [field, messages] of Object.entries(value)) {
    if (typeof messages === 'string') bag[field] = [messages]
    else if (Array.isArray(messages)) bag[field] = messages.filter((m) => typeof m === 'string')
  }

  return Object.keys(bag).length > 0 ? bag : undefined
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined
}
